/**
 * OpenFeature provider that wraps another (in our case, the
 * `FlagsmithClientProvider`) and consults the dev-tools overrides atom
 * before delegating.
 *
 * Why a wrapper instead of monkey-patching the inner provider?
 *  - The OpenFeature SDK caches resolution results per evaluation; we
 *    need a clean re-emit point (`ProviderEvents.ConfigurationChanged`)
 *    to make `useBooleanFlagValue` re-render after an override change.
 *  - We want to keep the upstream provider's behaviour (initialize,
 *    onContextChange, status events) bit-for-bit untouched in PROD —
 *    this wrapper is only ever installed in non-PROD builds.
 *
 * When Flagsmith is frozen (`isFlagsmithFrozen`: the native desktop e2e
 * lane) the wrapper also stops asking the delegate for values: a flag with no
 * override resolves to the default its call site passed. The native build's
 * overrides are a committed staging snapshot (`scripts/lib/e2e-flag-overrides.ts`),
 * so a Flagsmith rollout reaches the lane only through a snapshot refresh. The
 * delegate still initialises, so provider status, events and the live values
 * in the e2e flag snapshot are unchanged.
 */
import {
  OpenFeatureEventEmitter,
  ProviderEvents,
  type EvaluationContext,
  type JsonValue,
  type Logger,
  type Provider,
  type ProviderMetadata,
  type ResolutionDetails,
} from '@openfeature/web-sdk';

import { recordFlagsmithKey } from '@/features/flags-dev/discovery';
import {
  isDesktopE2eBuild,
  isFlagsmithFrozen,
} from '@/features/flags-dev/e2e-flag-defaults';
import {
  installE2eFlagSnapshot,
  recordFlagsmithResolution,
  type FlagEvaluationType,
  type LiveFlag,
} from '@/features/flags-dev/e2e-flag-snapshot';
import {
  getOverride,
  getVariantOverride,
  subscribeToOverrides,
  type FlagOverrideValue,
  type FlagProvider,
} from '@/features/flags-dev/overrides';
import { getSnapshotOverride } from '@/features/flags-dev/snapshot-override';

const OVERRIDE_REASON = 'STATIC';

/**
 * A variant override read as a number or JSON value, or `undefined` when there
 * is no variant or it does not parse. A frozen native build pins number and
 * JSON flags this way (`scripts/lib/e2e-flag-overrides.ts`), since Flagsmith
 * serves their values as text.
 */
const parseVariant = <T>(
  override: FlagOverrideValue,
  parse: (raw: string) => T | undefined
): T | undefined => {
  const raw = getVariantOverride(override);
  if (raw === undefined) return undefined;
  try {
    return parse(raw);
  } catch {
    return undefined;
  }
};
const FROZEN_REASON = 'DEFAULT';

export class OverrideOpenFeatureProvider implements Provider {
  readonly metadata: ProviderMetadata;
  readonly runsOn = 'client' as const;
  readonly events: OpenFeatureEventEmitter = new OpenFeatureEventEmitter();
  readonly hooks?: Provider['hooks'];

  /** Provider key in the overrides map (always 'flagsmith' for now). */
  private readonly _providerKey: FlagProvider;
  private readonly _delegate: Provider;
  /** True when Flagsmith is frozen: never read a live value from the delegate. */
  private readonly _frozen: boolean;
  private readonly _unsubscribe: () => void;
  /**
   * Re-emit ConfigurationChanged when the *delegate* fires it (e.g. when
   * Flagsmith finishes hydrating its cache), so consumers behind us still
   * see the same lifecycle.
   */
  private _delegateUnsubscribe?: () => void;

  constructor(
    delegate: Provider,
    providerKey: FlagProvider = 'flagsmith',
    frozen: boolean = isFlagsmithFrozen()
  ) {
    this._delegate = delegate;
    this._providerKey = providerKey;
    this._frozen = frozen;
    this.metadata = {
      name: `Override(${delegate.metadata?.name ?? 'unknown'})`,
    };
    this.hooks = delegate.hooks;
    this._unsubscribe = subscribeToOverrides(() => {
      this.events.emit(ProviderEvents.ConfigurationChanged, {
        message: 'flag overrides changed (dev-tools)',
      });
    });

    const delegateEvents = (delegate as { events?: OpenFeatureEventEmitter }).events;
    if (delegateEvents) {
      const forward = () => {
        this.events.emit(ProviderEvents.ConfigurationChanged, {
          message: 'delegate ConfigurationChanged',
        });
      };
      delegateEvents.addHandler(ProviderEvents.ConfigurationChanged, forward);
      this._delegateUnsubscribe = () => {
        delegateEvents.removeHandler(ProviderEvents.ConfigurationChanged, forward);
      };
    }

    if (isDesktopE2eBuild()) {
      installE2eFlagSnapshot({
        frozen,
        readLive: () =>
          (
            this.flagsmithClient as
              | { getAllFlags?: () => Record<string, LiveFlag> | undefined }
              | undefined
          )?.getAllFlags?.(),
      });
    }
  }

  async initialize(context?: EvaluationContext): Promise<void> {
    if (this._delegate.initialize) {
      await this._delegate.initialize(context);
    }
  }

  onContextChange(
    oldContext: EvaluationContext,
    newContext: EvaluationContext
  ): Promise<void> | void {
    return this._delegate.onContextChange?.(oldContext, newContext);
  }

  /** Pass-through accessor so dev-tools discovery can reach the wrapped Flagsmith client. */
  get flagsmithClient(): unknown {
    return (this._delegate as { flagsmithClient?: unknown }).flagsmithClient;
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<boolean> {
    return this._seen(
      flagKey,
      'boolean',
      this._resolveBoolean(flagKey, defaultValue, context, logger)
    );
  }

  private _resolveBoolean(
    flagKey: string,
    defaultValue: boolean,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<boolean> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      const snapshot = getSnapshotOverride(override);
      if (snapshot) {
        return {
          value: snapshot.enabled,
          reason: OVERRIDE_REASON,
          variant: override,
        };
      }
      return {
        value: override !== 'off',
        reason: OVERRIDE_REASON,
        variant: getVariantOverride(override) ?? override,
      };
    }
    return this._live(defaultValue, () =>
      this._delegate.resolveBooleanEvaluation(flagKey, defaultValue, context, logger)
    );
  }

  resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<string> {
    return this._seen(
      flagKey,
      'string',
      this._resolveString(flagKey, defaultValue, context, logger)
    );
  }

  private _resolveString(
    flagKey: string,
    defaultValue: string,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<string> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      const snapshot = getSnapshotOverride(override);
      if (snapshot) {
        return {
          value: snapshot.value === null ? defaultValue : String(snapshot.value),
          reason: OVERRIDE_REASON,
          variant: override,
        };
      }
      const variant = getVariantOverride(override);
      if (variant !== undefined) {
        return {
          value: variant,
          reason: OVERRIDE_REASON,
          variant,
        };
      }
      const resolved = this._live(defaultValue, () =>
        this._delegate.resolveStringEvaluation(flagKey, defaultValue, context, logger)
      );
      return {
        ...resolved,
        value: override === 'on' ? (resolved.value ?? defaultValue) : defaultValue,
        reason: OVERRIDE_REASON,
        variant: override,
      };
    }
    return this._live(defaultValue, () =>
      this._delegate.resolveStringEvaluation(flagKey, defaultValue, context, logger)
    );
  }

  resolveNumberEvaluation(
    flagKey: string,
    defaultValue: number,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<number> {
    return this._seen(
      flagKey,
      'number',
      this._resolveNumber(flagKey, defaultValue, context, logger)
    );
  }

  private _resolveNumber(
    flagKey: string,
    defaultValue: number,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<number> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      const snapshot = getSnapshotOverride(override);
      if (snapshot) {
        // The same parse as a `variant:` pin. A blank string is excluded
        // because `Number('')` is 0, not "no value".
        const value =
          typeof snapshot.value === 'number'
            ? snapshot.value
            : typeof snapshot.value === 'string' && snapshot.value.trim() !== ''
              ? Number(snapshot.value)
              : Number.NaN;
        return {
          value: Number.isFinite(value) ? value : defaultValue,
          reason: OVERRIDE_REASON,
          variant: override,
        };
      }
      const variant = parseVariant(override, (raw) => {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : undefined;
      });
      if (variant !== undefined) {
        return { value: variant, reason: OVERRIDE_REASON, variant: override };
      }
      const resolved = this._live(defaultValue, () =>
        this._delegate.resolveNumberEvaluation(flagKey, defaultValue, context, logger)
      );
      return {
        ...resolved,
        value: override !== 'off' ? resolved.value : defaultValue,
        reason: OVERRIDE_REASON,
        variant: override,
      };
    }
    return this._live(defaultValue, () =>
      this._delegate.resolveNumberEvaluation(flagKey, defaultValue, context, logger)
    );
  }

  resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<T> {
    return this._seen(
      flagKey,
      'object',
      this._resolveObject(flagKey, defaultValue, context, logger)
    );
  }

  private _resolveObject<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
    context: EvaluationContext,
    logger: Logger
  ): ResolutionDetails<T> {
    recordFlagsmithKey(flagKey);
    const override = getOverride(this._providerKey, flagKey);
    if (override) {
      const snapshot = getSnapshotOverride(override);
      if (snapshot) {
        let value: unknown = snapshot.value;
        if (typeof snapshot.value === 'string') {
          try {
            value = JSON.parse(snapshot.value);
          } catch {
            value = snapshot.value;
          }
        }
        return {
          value:
            value !== null && typeof value === 'object' ? (value as T) : defaultValue,
          reason: OVERRIDE_REASON,
          variant: override,
        };
      }
      const variant = parseVariant(override, (raw) => JSON.parse(raw) as T);
      if (variant !== undefined) {
        return { value: variant, reason: OVERRIDE_REASON, variant: override };
      }
      const resolved = this._live(defaultValue, () =>
        this._delegate.resolveObjectEvaluation(flagKey, defaultValue, context, logger)
      );
      return {
        ...resolved,
        value: override !== 'off' ? resolved.value : defaultValue,
        reason: OVERRIDE_REASON,
        variant: override,
      };
    }
    return this._live(defaultValue, () =>
      this._delegate.resolveObjectEvaluation(flagKey, defaultValue, context, logger)
    );
  }

  /** Records what a flag resolved to, for the e2e flag snapshot. */
  private _seen<T>(
    flagKey: string,
    evaluationType: FlagEvaluationType,
    details: ResolutionDetails<T>
  ): ResolutionDetails<T> {
    recordFlagsmithResolution(flagKey, evaluationType, details.value);
    return details;
  }

  /**
   * The delegate's resolution, or the call site's default when frozen. Every
   * read of a live value goes through here, including the `'on'` overrides
   * that keep the provider's value, so a frozen build has no path to it.
   */
  private _live<T>(
    defaultValue: T,
    resolve: () => ResolutionDetails<T>
  ): ResolutionDetails<T> {
    if (this._frozen) return { value: defaultValue, reason: FROZEN_REASON };
    return resolve();
  }

  /**
   * Stop listening to override / delegate change events and forward to
   * the delegate's own onClose. The OpenFeature SDK calls this when the
   * provider is unset; safe to call manually too. Returns a Promise as
   * required by the OpenFeature `Provider` interface.
   */
  async onClose(): Promise<void> {
    this._unsubscribe();
    this._delegateUnsubscribe?.();
    if (this._delegate.onClose) {
      await this._delegate.onClose();
    }
  }
}
